"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.stageForSnapshotLabel = exports.stagesForModel = exports.provisioningStageOrder = exports.provisioningStageById = exports.CONFIGURED_STAGE_IDS = exports.PROVISIONING_STAGES = void 0;
exports.holdsConfigured = holdsConfigured;
exports.configuredStepsDone = configuredStepsDone;
const hardwareCatalog_1 = require("./hardwareCatalog");
const ALL = ['payment', 'pos', 'accessory'];
/**
 * In journey order. Read against `ORDER_STAGES_V1` — these are the stages at
 * which somebody physically works a device, which is why the paperwork stages
 * (account, agreement, payment…) are not here.
 */
exports.PROVISIONING_STAGES = [
    {
        id: 'allocated',
        label: 'Stock allocated',
        hint: 'On the bench and reserved to this order — unboxing, inspection, anything done before the device is configured.',
        lanes: ALL,
    },
    /*
      CONFIGURATION IS THREE STAGES WEARING ONE NAME.
  
      The journey runs the three device lanes side by side, each with its own
      configure stage, because a reader, a till and a printer fail for different
      reasons. A template belongs to ONE model, so its lane is already known —
      `stagesForModel` resolves which of the three this device's steps land on.
    */
    {
        id: 'provision_payment',
        label: 'Configure hardware',
        hint: 'The card device’s own setup — before it is keyed to a TID.',
        lanes: ['payment'],
    },
    {
        id: 'provisioned',
        label: 'Configure hardware & install RubyTill',
        hint: 'The till’s setup, alongside its build. The RubyTill install is its own gate and is never a step here.',
        lanes: ['pos'],
        aliases: ['Configure hardware & install the OS'],
    },
    {
        id: 'provision_accessories',
        label: 'Configure hardware',
        hint: 'Whatever the device needs to be finished — a printer paired and test-fed, a scanner programmed.',
        lanes: ['accessory'],
    },
    {
        id: 'device_couple',
        label: 'Couple the register and its payment device',
        hint: 'Worked once the till has a register and the reader is keyed. Only a till or a card device is ever part of a couple.',
        lanes: ['payment', 'pos'],
    },
    {
        id: 'test_transaction',
        label: 'Testing',
        hint: 'What must be proved on this device before the test sale is run.',
        lanes: ALL,
    },
    {
        id: 'packed',
        label: 'Packing',
        hint: 'Packing instructions for this device — what goes in the box with it, and how.',
        lanes: ALL,
    },
    {
        id: 'shipped',
        label: 'Before marking shipped',
        hint: 'The last checks on this device before the order can be marked shipped.',
        lanes: ALL,
    },
];
/**
 * THE STAGES WHOSE STEPS HOLD A UNIT OUT OF `configured`.
 *
 * Allocation and configuration. Coupling, testing, packing and "before marking
 * shipped" are worked AFTER a device is configured — a checklist step there
 * used to hold `configured` back, so the order's "Configure hardware" stage
 * could never complete before packing. Those steps are still enforced: the
 * packing gate requires every required step on the unit, whatever its stage.
 */
exports.CONFIGURED_STAGE_IDS = [
    'allocated', 'provision_payment', 'provisioned', 'provision_accessories',
];
const BY_ID = new Map(exports.PROVISIONING_STAGES.map((s) => [s.id, s]));
const provisioningStageById = (id) => BY_ID.get(id);
exports.provisioningStageById = provisioningStageById;
/** Journey position, for sorting. Unknown ids sort last rather than to the front. */
const provisioningStageOrder = (id) => {
    const i = exports.PROVISIONING_STAGES.findIndex((s) => s.id === id);
    return i < 0 ? 999 : i;
};
exports.provisioningStageOrder = provisioningStageOrder;
/**
 * The stages a given model's checklist may use.
 *
 * A model with no category answers no lane (`deviceLane` returns null rather
 * than guessing), and gets the lane-independent stages only — never a
 * configure stage picked at random.
 */
const stagesForModel = (model) => {
    const lane = (0, hardwareCatalog_1.deviceLane)(model);
    if (!lane)
        return exports.PROVISIONING_STAGES.filter((s) => s.lanes.length === 3);
    return exports.PROVISIONING_STAGES.filter((s) => s.lanes.includes(lane));
};
exports.stagesForModel = stagesForModel;
const answersTo = (stage, label) => stage.label === label || !!stage.aliases?.includes(label);
/**
 * Which stage a unit's step belongs to, for a SNAPSHOT.
 *
 * A pinned step carries its category LABEL, not its id — that is what the
 * service copies onto the unit. Two stages share the label "Configure
 * hardware" (payment and accessories), so a label alone cannot identify a
 * stage; the device's lane breaks the tie, which is exactly how the label was
 * chosen in the first place.
 *
 * `aliases` are matched as well as the current label, so a device that pinned
 * its plan before a stage was reworded still lands on that stage. The device's
 * OWN lane is tried first and the full list only as a fallback, so a shared
 * label never pulls a step onto a stage its device does not have.
 */
const stageForSnapshotLabel = (label, model) => (0, exports.stagesForModel)(model).find((s) => answersTo(s, label))
    || exports.PROVISIONING_STAGES.find((s) => answersTo(s, label));
exports.stageForSnapshotLabel = stageForSnapshotLabel;
/**
 * DOES THIS STEP HOLD A UNIT OUT OF `configured`?
 *
 * Only a REQUIRED step can. Of those, a step filed under an allocation or
 * configure stage holds it, and one filed under a later stage (couple, testing,
 * packing, before shipped) does not.
 *
 * A step whose category matches NO known stage — an older free-text category
 * such as the default template's "Configuring", or a typo — HOLDS it. Nothing
 * becomes configured by accident because its group could not be read.
 *
 * Deliberately lane-free: every label a later stage answers to is distinct
 * from every configure label, so the lane never changes the answer.
 */
function holdsConfigured(step) {
    if (!step?.required)
        return false;
    const label = typeof step.category === 'string' ? step.category : '';
    const stage = exports.PROVISIONING_STAGES.find((s) => answersTo(s, label));
    if (!stage)
        return true;
    return exports.CONFIGURED_STAGE_IDS.includes(stage.id);
}
/**
 * Every step that holds `configured` is done. Vacuously true for an empty
 * checklist, exactly as "every required step done" always was.
 */
function configuredStepsDone(steps) {
    return (steps ?? []).filter(holdsConfigured).every((s) => !!s.done);
}
//# sourceMappingURL=provisioningStages.js.map